import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Controller, useForm, type Control, type UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Checkbox, Chip, Select, Toast, type ToastType } from "@legal-500/legal500-design-system";
import { usePermissions } from "@/hooks/usePermissions";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  researchCms,
  fetchAllEditionBpas,
  fetchAllEditions,
  type EditionBpa,
  type EditionBpaUpdateRequest,
  type EditionConfig,
  type EditionConfigUpdateRequest,
  type Researcher,
  type ResearchProduct,
} from "@/api/researchCms.service";
import { userManagement } from "@/api/userManagement.service";
import { UnauthorizedView } from "../../../components/UnauthorizedView";
import {
  CmsConfirmDialog,
  type CmsConfirmAffectedGroup,
} from "../../../components/ResearchCms/CmsConfirmDialog/CmsConfirmDialog";
import { UnsavedChangesModal } from "../../../components/ResearchCms/UnsavedChangesModal/UnsavedChangesModal";
import { CreateResearchProductModal } from "../../../components/ResearchCms/CreateResearchProductModal/CreateResearchProductModal";
import { CmsGroupedTable } from "../../../components/ResearchCms/CmsGroupedTable/CmsGroupedTable";
import { DangerZone } from "../../../components/ResearchCms/DangerZone/DangerZone";
import { CmsEmptyState } from "../../../components/ResearchCms/CmsEmptyState/CmsEmptyState";
import { CmsStackedBar } from "../../../components/ResearchCms/CmsStackedBar/CmsStackedBar";
import { LanguageSwitcher } from "../../../components/ResearchCms/LanguageSwitcher/LanguageSwitcher";
import {
  StatusBadge,
  getStatusLabel,
} from "../../../components/ResearchCms/StatusBadge/StatusBadge";
import {
  AsyncSearchableSelect,
  type AsyncSelectOption,
} from "../../../components/Form/AsyncSearchableSelect/AsyncSearchableSelect";
import SelectAllCheckbox from "../../../components/Form/SelectAllCheckbox/SelectAllCheckbox";
import { ROUTES } from "../../../routes";
import { CmsLayout } from "./components/CmsLayout";
import { BulkAssignBar } from "./components/BulkAssignBar";
import { CmsBpaMergeDialog, splitOpenQuery } from "./components/BpaMerge/CmsBpaMergeDialog";
import { ScheduledForMergeBadge } from "./components/BpaMerge/ScheduledForMergeBadge";
import {
  inMergeBlockedReason,
  mergeActionState,
  mergeBadge,
  mergeBlockedHubHref,
  toMergeRow,
} from "./utils/bpaMerge";
import { useNavigationGuard } from "./hooks/useNavigationGuard";
import { toBpaRows, type BpaRow } from "./utils/bpaRows";
import { selectAll, selectAllState, toggle, clear } from "./utils/rowSelection";
import { describeEditionStatusChange, type EditionStatusChange } from "./utils/editionStatusChange";
import { resolveAssignee } from "./utils/assigneeResolution";
import { resolveArrivalEdition } from "./utils/arrivalEdition";
import { languageName } from "./utils/languageName";
import { researcherOptionMatchesSearch } from "./utils/researcherOption";
import { assigneeOptions } from "./utils/assigneeOptions";
import { applyBulkAssignment, type AssignmentField } from "./utils/applyBulkAssignment";
import { withSaveTimeout } from "./saveRequestTimeout";
import { buildSaveErrorMessage } from "./utils/saveErrorMessage";
import { buildBulkAssignPayload } from "./utils/bulkAssignPayload";
import { mergeFormRows } from "./utils/mergeFormRows";
import { BPA_INITIAL_PAGE_SIZE, nextBpaRequest } from "./utils/bpaPageRequests";
import { editionProgressSummary, sumSubmissionCounts } from "./utils/editionProgress";
import { bpaRowLink } from "./utils/bpaRowLink";
import { namedListPreview } from "./utils/namedListPreview";
import { TemplateFieldsPreview } from "./components/TemplateFieldsPreview/TemplateFieldsPreview";
import {
  BPA_STATUS_VALUES,
  EDITION_STATUS_VALUES,
  manageFormSchema,
  toRowStatus,
  type BpaRowFormValues,
  type ManageFormValues,
} from "./schemas/manageFormSchemas";
import shared from "./components/cmsShared.module.css";
import styles from "./CmsManagePage.module.css";

function buildDefaultValues(
  edition: EditionConfig,
  rows: BpaRow[],
  productSlugs: string[],
): ManageFormValues {
  return {
    editionConfigId: edition.id,
    status: edition.status,
    leadAssigneeId: edition.leadAssigneeId,
    submissionCloseDate: toDateInputValue(edition.submissionCloseDate),
    publicationFromDate: toDateInputValue(edition.publicationFromDate),
    publicationToDate: toDateInputValue(edition.publicationToDate),
    rows: rows.map(row => ({
      id: row.id,
      assignedResearcherId: row.assignedResearcherId,
      assignedEditorId: row.assignedEditorId,
      status: toRowStatus(row.status),
    })),
    productSlugs,
  };
}

function toDateInputValue(value: string | null): string | null {
  return value ? value.slice(0, 10) : null;
}

function toIsoTimestamp(value: string | null): string | null {
  return value ? `${value}T00:00:00.000Z` : null;
}

function byProductSortOrder(
  a: Pick<ResearchProduct, "slug" | "sortOrder">,
  b: Pick<ResearchProduct, "slug" | "sortOrder">,
): number {
  return a.sortOrder - b.sortOrder || a.slug.localeCompare(b.slug);
}

/**
 * The selection after one tick, kept in catalogue order. Order-insensitive comparison is enough for the
 * save, but the form's dirty check is a deep equal — so a product unticked and ticked again must land
 * back in the same place, or the form reads as edited with nothing changed.
 */
function toggleProductSlug(
  products: ResearchProduct[],
  selected: string[],
  slug: string,
  checked: boolean,
): string[] {
  return products
    .filter(product => (product.slug === slug ? checked : selected.includes(product.slug)))
    .map(product => product.slug);
}

function sameProductSlugs(a: string[], b: string[]): boolean {
  const saved = new Set(b);
  return a.length === b.length && a.every(slug => saved.has(slug));
}

const EDITION_DATE_FIELDS = [
  { name: "submissionCloseDate", label: "Submission close date" },
  { name: "publicationFromDate", label: "Publication from" },
  { name: "publicationToDate", label: "Publication to" },
] as const;

type EditionDateFieldName = (typeof EDITION_DATE_FIELDS)[number]["name"];

function EditionDateField({
  control,
  name,
  label,
}: {
  control: Control<ManageFormValues>;
  name: EditionDateFieldName;
  label: string;
}) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <div className={styles.dateField}>
          <label className={styles.dateLabel} htmlFor={name}>
            {label}
          </label>
          <input
            id={name}
            type="date"
            className={`${shared.textInput} ${styles.dateInput}`}
            value={field.value ?? ""}
            onChange={e => field.onChange(e.target.value || null)}
          />
          {fieldState.error && (
            <span className={shared.fieldError}>{fieldState.error.message}</span>
          )}
        </div>
      )}
    />
  );
}

/**
 * research-api's edition-bpas sort enum already accepts `ranking_entity_asc` (see
 * research-api/src/schemas/editionBpa.ts), but staff-ui's generated OpenAPI types
 * (`src/types/research-api.generated.ts`) are a snapshot of a stale `openapi.json` that
 * predates that addition, so the generated query type does not include it. Regenerating that
 * snapshot is outside this page's two files, so the literal is asserted through instead of
 * widening the generated type.
 */
const EDITION_BPA_SORT = "ranking_entity_asc";

type EditionBpaQuery = Parameters<typeof researchCms.getEditionBpas>[0];

function withRankingEntitySort(query: Omit<EditionBpaQuery, "sort">): EditionBpaQuery {
  return { ...query, sort: EDITION_BPA_SORT };
}

type EditionBpasState = {
  bpas: EditionBpa[];
  loading: boolean;
  hasMore: boolean;
  total: number;
  error: string | null;
};

const INITIAL_BPAS_STATE: EditionBpasState = {
  bpas: [],
  loading: true,
  hasMore: false,
  total: 0,
  error: null,
};

const PLACEHOLDER_ROW_CEILING = 10;
const PLACEHOLDER_ROWS_ON_FIRST_LOAD = 4;

/**
 * How many skeleton rows to draw while an edition's practice areas load.
 *
 * Sized from the rows currently on screen, so an edition switch reserves the height it is giving up and
 * the sections below hold still. The incoming count is not knowable here — the edition-configs response
 * carries no BPA count — so an edition with a very different number of rows still moves a little as they
 * land. Far less than the table collapsing to a single line, which is what this replaces.
 */
function placeholderRowCount(outgoing: number): number {
  const rows = outgoing > 0 ? outgoing : PLACEHOLDER_ROWS_ON_FIRST_LOAD;
  return Math.min(rows, PLACEHOLDER_ROW_CEILING);
}

/**
 * Holds the practice-area table's footprint while its rows load, so switching edition does not collapse
 * the page to a single line and expand it again. Deliberately not the previous edition's rows dimmed:
 * those are still interactive, and a row edited in that window would be written against the edition the
 * user just left.
 */
function BpaTablePlaceholder({ rowCount }: { rowCount: number }) {
  return (
    <div className={styles.tablePlaceholder} aria-busy="true">
      <span className={styles.tablePlaceholderLabel}>Loading practice areas…</span>
      <div className={styles.placeholderHeader} />
      {Array.from({ length: rowCount }, (_, index) => (
        <div className={styles.placeholderRow} key={index} />
      ))}
    </div>
  );
}

/**
 * The count with no total. DEV-2695 owns the expected figure and it is not reachable from
 * research-api, so no denominator is shown rather than an invented one (tasks/0012). An unknown
 * count reads as unknown — printing 0 would assert a figure we do not have.
 */
function formatSubmissionCount(count: number | undefined): string {
  if (count === undefined) return "—";
  return count === 1 ? "1 submission" : `${count} submissions`;
}

function humaniseCode(value: string): string {
  const spaced = value.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function formatDate(value: string | null): string {
  if (!value) return "Not set";
  return new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function rollbackDestroysFinishedWork(change: EditionStatusChange): boolean {
  return change.direction === "backward" && change.movingCount > 0 && change.movingRowsAreComplete;
}

const STATUS_CHANGE_AFFECTED_CAP = 10;

function buildStatusChangeAffected(change: EditionStatusChange): CmsConfirmAffectedGroup[] {
  const moving = namedListPreview(change.movingRows, STATUS_CHANGE_AFFECTED_CAP);
  const overridden = namedListPreview(change.overriddenRows, STATUS_CHANGE_AFFECTED_CAP);

  return [
    {
      heading: "Will move",
      items: moving.shown,
      remaining: Math.max(change.movingCount - moving.shown.length, 0),
    },
    {
      heading: "Will not be affected",
      items: overridden.shown,
      remaining: Math.max(change.overriddenCount - overridden.shown.length, 0),
    },
  ];
}

function buildStatusChangeWarning(change: EditionStatusChange, nextStatusLabel: string): string {
  const moveVerb = change.direction === "backward" ? "move back to" : "move to";
  const base = `${change.movingCount} practice area(s) will ${moveVerb} ${nextStatusLabel}.`;
  const unaffected = `${change.overriddenCount} practice area(s) are at a different status and will not be affected.`;
  if (!rollbackDestroysFinishedWork(change)) {
    return `${base} ${unaffected}`;
  }
  return `${base} ${unaffected} Practice areas already marked complete will lose that status, and this cannot be undone.`;
}

type StatusChangeRequest = {
  nextStatus: ManageFormValues["status"];
  commit: (value: ManageFormValues["status"]) => void;
  change: EditionStatusChange;
};

/**
 * A validated save held while its product changes are confirmed. `values` is what `handleSubmit` passed
 * through validation, so confirming writes exactly what was checked rather than re-reading the form.
 */
type ProductsChangeRequest = {
  values: ManageFormValues;
  adding: ResearchProduct[];
  removing: ResearchProduct[];
};

function buildProductsChangeAffected(request: ProductsChangeRequest): CmsConfirmAffectedGroup[] {
  return [
    { heading: "Adding", products: request.adding },
    { heading: "Removing", products: request.removing },
  ]
    .filter(group => group.products.length > 0)
    .map(group => ({
      heading: group.heading,
      items: group.products.map(product => ({ id: product.slug, name: product.label })),
      remaining: 0,
    }));
}

type EditionFormValues = Pick<
  ManageFormValues,
  "status" | "leadAssigneeId" | "submissionCloseDate" | "publicationFromDate" | "publicationToDate"
>;

/**
 * The values a failed save holds on to so the reset that follows the refetch can put them back.
 *
 * `editionConfigId` is what the write targeted, and it is load-bearing: the reset effect also runs on an
 * edition switch, so without it a failed save's values are replayed onto whichever edition is selected
 * next and a following Save writes them to that edition's row (tasks/0029).
 */
type RetainedEdits = {
  editionConfigId: string;
  edition: EditionFormValues | null;
  rows: Record<string, BpaRowFormValues>;
  productSlugs: string[] | null;
};

type PendingWrite =
  | { kind: "edition"; request: Promise<unknown> }
  | {
      kind: "row";
      row: BpaRowFormValues;
      request: Promise<unknown>;
    }
  | {
      kind: "bulk";
      rows: BpaRowFormValues[];
      request: Promise<unknown>;
    }
  | {
      kind: "products";
      productSlugs: string[];
      request: Promise<unknown>;
    };

function collectRetainedEdits(data: ManageFormValues, failures: PendingWrite[]): RetainedEdits {
  const rows: Record<string, BpaRowFormValues> = {};
  let edition: RetainedEdits["edition"] = null;
  let productSlugs: RetainedEdits["productSlugs"] = null;

  for (const failure of failures) {
    if (failure.kind === "row") {
      rows[failure.row.id] = failure.row;
      continue;
    }
    if (failure.kind === "bulk") {
      for (const row of failure.rows) {
        rows[row.id] = row;
      }
      continue;
    }
    if (failure.kind === "products") {
      productSlugs = failure.productSlugs;
      continue;
    }
    edition = {
      status: data.status,
      leadAssigneeId: data.leadAssigneeId,
      submissionCloseDate: data.submissionCloseDate,
      publicationFromDate: data.publicationFromDate,
      publicationToDate: data.publicationToDate,
    };
  }

  return { editionConfigId: data.editionConfigId, edition, rows, productSlugs };
}

function applyRetainedEdits(
  form: UseFormReturn<ManageFormValues>,
  serverValues: ManageFormValues,
  retained: RetainedEdits,
) {
  if (retained.edition) {
    form.setValue("status", retained.edition.status, { shouldDirty: true });
    form.setValue("leadAssigneeId", retained.edition.leadAssigneeId, { shouldDirty: true });
    for (const { name } of EDITION_DATE_FIELDS) {
      form.setValue(name, retained.edition[name], { shouldDirty: true });
    }
  }
  if (retained.productSlugs) {
    form.setValue("productSlugs", retained.productSlugs, { shouldDirty: true });
  }
  serverValues.rows.forEach((row, index) => {
    const retainedRow = retained.rows[row.id];
    if (!retainedRow) return;
    form.setValue(`rows.${index}.assignedResearcherId`, retainedRow.assignedResearcherId, {
      shouldDirty: true,
    });
    form.setValue(`rows.${index}.assignedEditorId`, retainedRow.assignedEditorId, {
      shouldDirty: true,
    });
    form.setValue(`rows.${index}.status`, retainedRow.status, { shouldDirty: true });
  });
}

type BpaResearcherCellProps = {
  control: Control<ManageFormValues>;
  index: number;
  researchers: Researcher[];
  resolvedNames: Record<string, string>;
  researchersKnown: boolean;
  canAssign: boolean;
};

function BpaResearcherCell({
  control,
  index,
  researchers,
  resolvedNames,
  researchersKnown,
  canAssign,
}: BpaResearcherCellProps) {
  const options: AsyncSelectOption[] = assigneeOptions(researchers);

  return (
    <Controller
      name={`rows.${index}.assignedResearcherId`}
      control={control}
      render={({ field }) => {
        const resolved = resolveAssignee({
          assigneeId: field.value,
          researchers,
          resolvedNames,
        });

        const lapsedBadge = resolved.isFlagged && researchersKnown && (
          <span className={styles.assigneeWarning}>
            <Chip color="warning" size="small" variant="outlined">
              Not currently a researcher
            </Chip>
          </span>
        );

        if (!canAssign) {
          return (
            <div className={styles.researcherCell}>
              <span>{resolved.displayName || "Unassigned"}</span>
              {lapsedBadge}
            </div>
          );
        }

        return (
          <div className={styles.researcherCell}>
            <AsyncSearchableSelect
              name={`researcher-${index}`}
              label=""
              value={field.value}
              onChange={value => field.onChange(value)}
              onSearch={async query => {
                const term = query.trim().toLowerCase();
                return term
                  ? options.filter(option => researcherOptionMatchesSearch(option, term))
                  : options;
              }}
              minSearchLength={0}
              placeholder="Unassigned"
              initialOption={
                resolved.isAssigned && field.value
                  ? (options.find(option => option.value === field.value) ?? {
                      value: field.value,
                      label: resolved.displayName,
                    })
                  : null
              }
            />
            {lapsedBadge}
          </div>
        );
      }}
    />
  );
}

type BpaEditorCellProps = {
  control: Control<ManageFormValues>;
  index: number;
  researchers: Researcher[];
  resolvedNames: Record<string, string>;
  canAssign: boolean;
};

/**
 * Mirrors BpaResearcherCell's structure — badge-free static text without the write permission,
 * an AsyncSearchableSelect drawing from the same assigneeOptions source with it. Deliberately
 * without the lapsed-assignee badge BpaResearcherCell shows: nothing in this column's requirements
 * asks for it.
 */
function BpaEditorCell({
  control,
  index,
  researchers,
  resolvedNames,
  canAssign,
}: BpaEditorCellProps) {
  const options: AsyncSelectOption[] = assigneeOptions(researchers);

  return (
    <Controller
      name={`rows.${index}.assignedEditorId`}
      control={control}
      render={({ field }) => {
        const resolved = resolveAssignee({
          assigneeId: field.value,
          researchers,
          resolvedNames,
        });

        if (!canAssign) {
          return (
            <div className={styles.researcherCell}>
              <span>{resolved.displayName || "Unassigned"}</span>
            </div>
          );
        }

        return (
          <div className={styles.researcherCell}>
            <AsyncSearchableSelect
              name={`editor-${index}`}
              label=""
              value={field.value}
              onChange={value => field.onChange(value)}
              onSearch={async query => {
                const term = query.trim().toLowerCase();
                return term
                  ? options.filter(option => researcherOptionMatchesSearch(option, term))
                  : options;
              }}
              minSearchLength={0}
              placeholder="Unassigned"
              initialOption={
                resolved.isAssigned && field.value
                  ? (options.find(option => option.value === field.value) ?? {
                      value: field.value,
                      label: resolved.displayName,
                    })
                  : null
              }
            />
          </div>
        );
      }}
    />
  );
}

/**
 * One column for `research_edition_bpa.status`, which is one field. Shows the badge at rest and swaps
 * to the select once the cell is activated, so the table keeps reading as a set of statuses rather
 * than a column of controls. Without the write permission the badge is plain text and nothing is
 * interactive, matching AC 7. Entirely local to this cell — no shared component or stylesheet.
 */
function BpaStatusCell({
  control,
  index,
  bpaName,
  canEdit,
}: {
  control: Control<ManageFormValues>;
  index: number;
  bpaName: string;
  canEdit: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);

  return (
    <Controller
      name={`rows.${index}.status`}
      control={control}
      render={({ field }) => {
        if (!canEdit) {
          return <StatusBadge status={field.value} />;
        }

        if (!isEditing) {
          return (
            <span
              id={`row-status-${index}`}
              className={styles.statusBadgeButton}
              role="button"
              tabIndex={0}
              title={`Change status for ${bpaName}`}
              aria-label={`Change status for ${bpaName}`}
              aria-haspopup="listbox"
              onClick={() => setIsEditing(true)}
              onKeyDown={event => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                setIsEditing(true);
              }}
            >
              <StatusBadge status={field.value} interactive />
            </span>
          );
        }

        return (
          <div className={styles.statusCell}>
            <Select
              id={`row-status-${index}`}
              aria-label={`Status for ${bpaName}`}
              fullWidth
              autoFocus
              value={field.value}
              onChange={e => field.onChange(e.target.value as BpaRowFormValues["status"])}
              onBlur={() => setIsEditing(false)}
              options={BPA_STATUS_VALUES.map(value => ({
                value,
                label: getStatusLabel(value),
              }))}
            />
          </div>
        );
      }}
    />
  );
}

export function CmsManagePage() {
  const { id: publicationConfigId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const { hasFlag } = useFeatureFlags();
  const isAdmin = hasPermission("staff.research.admin");
  const canAssignResearcher = hasPermission("staff.research.edit");
  // The permission the write itself requires, at both the staff-bff proxy and research-api. Gating on
  // `staff.research.change_status` would offer a control the proxy refuses, so this is the gate even
  // though that permission exists and nothing reads it (decided 2026-08-11; see tasks/0027).
  const canEditRowStatus = hasPermission("staff.research.edit");
  // Bulk selection is gated on the write permission that action requires, not on read or edit —
  // selecting rows exists to feed a bulk assignment action, not to browse.
  const canSelectRows = hasPermission("staff.research.create");
  // Product enablement replaces the WordPress setting, so it stays out of sight — no tab, no requests —
  // until the flag hands it over.
  const productsEnabled = hasFlag("research-cms-1-enabled");

  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(clear);
  const [selectedEditionConfigId, setSelectedEditionConfigId] = useState<string | null>(null);
  const [pendingEditionSwitchId, setPendingEditionSwitchId] = useState<string | null>(null);
  const [statusChangeRequest, setStatusChangeRequest] = useState<StatusChangeRequest | null>(null);
  const [productsChangeRequest, setProductsChangeRequest] = useState<ProductsChangeRequest | null>(
    null,
  );
  const [cascadeStatusToBpas, setCascadeStatusToBpas] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [resolvedNames, setResolvedNames] = useState<Record<string, string>>({});
  const showTranslation = searchParams.get("lang") === "en-GB";
  const attemptedLookups = useRef<Set<string>>(new Set());
  const [bpaRefetchKey] = useState(0);
  const [manageTab, setManageTab] = useState<"publication" | "practiceAreas" | "products">(
    "publication",
  );
  const retainedFailedEdits = useRef<RetainedEdits | null>(null);
  // Tracks the edition the row selection belongs to. Every route to a new edition — the select, arrival
  // resolution, and the post-save refetch — funnels through the effect below, so keying the clear on
  // this ref (rather than on `switchEdition` or unconditionally in the effect) catches all of them
  // without wiping the selection on a same-edition refetch that merely produces a new `bpaRows` array.
  const lastSeenEditionConfigIdRef = useRef<string | null>(null);
  const isLoadMoreRef = useRef(false);
  const [editionBpasState, setEditionBpasState] = useState<EditionBpasState>(INITIAL_BPAS_STATE);
  const [loadingMoreBpas, setLoadingMoreBpas] = useState(false);
  // `form.setValue` does not by itself re-render CmsManagePage: the table's cells resolve their
  // control via a non-reactive `form.getValues("rows").findIndex(...)` read taken while this
  // component's own function body runs, and setValue with its default (non-dirtying) options
  // notifies no subscriber that would cause that to happen again. Bumping this after a load-more
  // merge forces the one extra render the newly merged rows need to stop resolving to index -1.
  const [, bumpFormRowsRevision] = useReducer((count: number) => count + 1, 0);

  const canRead = hasPermission("staff.research.read");

  const publicationQuery = useQuery({
    queryKey: ["research-publication-config", publicationConfigId],
    queryFn: () => researchCms.getPublication(publicationConfigId ?? ""),
  });
  const publicationConfig = publicationQuery.data?.publicationConfig;
  const directoryId = publicationConfig?.directoryId;

  const editionsQuery = useQuery({
    queryKey: ["research-editions-by-directory", directoryId],
    queryFn: () => (directoryId ? fetchAllEditions({ directoryId }) : Promise.resolve([])),
  });
  const editions = useMemo(() => editionsQuery.data ?? [], [editionsQuery.data]);

  useEffect(() => {
    if (selectedEditionConfigId || editions.length === 0) return;
    const arrivalEdition = resolveArrivalEdition({
      requestedEditionConfigId: searchParams.get("edition"),
      editions,
    });
    setSelectedEditionConfigId(arrivalEdition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editions, selectedEditionConfigId]);

  const selectedEdition = editions.find(edition => edition.id === selectedEditionConfigId);

  // BPA fetching with load-more support. The initial load brings the first page;
  // "Load more" appends subsequent pages; refetch after save uses fetchAllEditionBpas
  // so the form always resets against the complete dataset.
  useEffect(() => {
    if (!selectedEdition) {
      setEditionBpasState({ bpas: [], loading: false, hasMore: false, total: 0, error: null });
      return;
    }
    let cancelled = false;
    setEditionBpasState(INITIAL_BPAS_STATE);

    const initialRequest =
      nextBpaRequest({ loaded: 0, total: null, mode: "initial" }) ??
      ({ limit: BPA_INITIAL_PAGE_SIZE, offset: 0 } as const);

    researchCms
      .getEditionBpas(
        withRankingEntitySort({
          editionId: selectedEdition.editionId,
          limit: initialRequest.limit,
          offset: initialRequest.offset,
        }),
      )
      .then(result => {
        if (cancelled) return;
        setEditionBpasState({
          bpas: result.editionBpas,
          loading: false,
          hasMore: result.pagination.hasMore,
          total: result.pagination.total,
          error: null,
        });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setEditionBpasState(prev => ({
            ...prev,
            loading: false,
            error: err instanceof Error ? err.message : "Failed to load practice areas",
          }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedEdition?.editionId, bpaRefetchKey]);

  async function loadNextBpaPage(mode: "more" | "all") {
    if (!selectedEdition || loadingMoreBpas || !editionBpasState.hasMore) return;
    const request = nextBpaRequest({
      loaded: editionBpasState.bpas.length,
      total: editionBpasState.total,
      mode,
    });
    if (!request) return;

    setLoadingMoreBpas(true);
    try {
      const result = await researchCms.getEditionBpas(
        withRankingEntitySort({
          editionId: selectedEdition.editionId,
          limit: request.limit,
          offset: request.offset,
        }),
      );
      isLoadMoreRef.current = true;
      setEditionBpasState(prev => ({
        ...prev,
        bpas: [...prev.bpas, ...result.editionBpas],
        hasMore: result.pagination.hasMore,
        total: result.pagination.total,
      }));
    } catch (err) {
      setEditionBpasState(prev => ({
        ...prev,
        error: err instanceof Error ? err.message : "Failed to load more practice areas",
      }));
    } finally {
      setLoadingMoreBpas(false);
    }
  }

  function handleLoadMore() {
    return loadNextBpaPage("more");
  }

  function handleLoadAll() {
    return loadNextBpaPage("all");
  }

  async function refetchBpas() {
    if (!selectedEdition) return;
    const allBpas = await fetchAllEditionBpas({
      editionId: selectedEdition.editionId,
      sort: EDITION_BPA_SORT,
    });
    setEditionBpasState({
      bpas: allBpas,
      loading: false,
      hasMore: false,
      total: allBpas.length,
      error: null,
    });
  }

  const editionBpas = editionBpasState.bpas;

  const bpaStatsQuery = useQuery({
    queryKey: ["research-bpa-stats", selectedEdition?.editionId],
    queryFn: () =>
      selectedEdition
        ? researchCms.getBpaStats({ editionId: selectedEdition.editionId })
        : Promise.resolve(null),
  });

  const submissionCountByBpaId = useMemo(() => {
    const counts = new Map<string, number>();
    for (const stat of bpaStatsQuery.data?.researchBpaStats ?? []) {
      counts.set(stat.bpaId, stat.submissionCount);
    }
    return counts;
  }, [bpaStatsQuery.data]);

  const editionBpaIdByBpaId = useMemo(() => {
    const map = new Map<string, string>();
    for (const editionBpa of editionBpas) {
      if (!map.has(editionBpa.bpaId)) map.set(editionBpa.bpaId, editionBpa.id);
    }
    return map;
  }, [editionBpas]);

  const hasTranslations = editionBpas.some(eb => eb.outputName || eb.englishName);

  const bpaRows = useMemo(
    () =>
      toBpaRows(
        editionBpas.map(editionBpa => {
          const outputN = editionBpa.outputName ?? null;
          const englishN = editionBpa.englishName ?? null;
          return {
            id: editionBpa.bpaId,
            name: showTranslation
              ? (englishN ?? editionBpa.bpaName)
              : (outputN ?? editionBpa.bpaName),
            rankingEntityId: editionBpa.rankingEntityId,
            rankingEntityName: editionBpa.rankingEntityName,
            status: editionBpa.status,
            assignedResearcherId: editionBpa.assignedResearcherId,
            assignedEditorId: editionBpa.assignedEditorId,
          };
        }),
      ),
    [editionBpas, showTranslation],
  );

  const mergeRows = useMemo(() => editionBpas.map(toMergeRow), [editionBpas]);
  const tickedMergeRows = mergeRows.filter(row => selectedRowKeys.has(row.bpaId));
  const mergeFlagOn = hasFlag("research-cms-1-enabled");
  const splitCheckEnabled =
    mergeFlagOn && tickedMergeRows.length >= 2 && tickedMergeRows.length <= 5;
  const tickedSplitQuery = useQuery({
    ...splitOpenQuery(selectedEdition?.editionId ?? ""),
    enabled: splitCheckEnabled && Boolean(selectedEdition),
  });

  const researchersQuery = useQuery({
    queryKey: ["research-researchers"],
    queryFn: () => researchCms.getResearchers(),
  });
  const researchers = researchersQuery.data?.researchers ?? [];
  const researchersKnown = !researchersQuery.isLoading && !researchersQuery.error;
  const researchersUnavailable = !researchersQuery.isLoading && Boolean(researchersQuery.error);

  const templateType = publicationConfig?.templateType ?? null;
  const templateFieldsQuery = useQuery({
    queryKey: ["research-template-fields", templateType],
    queryFn: () =>
      templateType ? researchCms.getTemplateFields(templateType) : Promise.resolve(null),
  });
  const templateFields = templateFieldsQuery.data?.templateFields ?? [];

  const researchProductsQuery = useQuery({
    queryKey: ["research-products"],
    queryFn: () => researchCms.getResearchProducts(),
    enabled: productsEnabled && Boolean(selectedEdition),
  });
  const researchProducts = useMemo(
    () => [...(researchProductsQuery.data?.products ?? [])].sort(byProductSortOrder),
    [researchProductsQuery.data],
  );

  const editionProductsQuery = useQuery({
    queryKey: ["research-edition-config-products", selectedEdition?.id],
    queryFn: () =>
      selectedEdition
        ? researchCms.getEditionConfigProducts(selectedEdition.id)
        : Promise.resolve(null),
    enabled: productsEnabled && Boolean(selectedEdition),
  });
  const savedProductSlugs = useMemo(
    () =>
      [...(editionProductsQuery.data?.products ?? [])]
        .sort(byProductSortOrder)
        .map(product => product.slug),
    [editionProductsQuery.data],
  );
  // Every response carries a fresh requestId, so the data object changes on a refetch that changed
  // nothing. The effect below keys on the slugs themselves instead.
  const savedProductSlugsKey = savedProductSlugs.join(",");

  const form = useForm<ManageFormValues>({
    resolver: zodResolver(manageFormSchema),
    defaultValues: {
      editionConfigId: "",
      status: "setup",
      leadAssigneeId: null,
      submissionCloseDate: null,
      publicationFromDate: null,
      publicationToDate: null,
      rows: [],
      productSlugs: [],
    },
  });

  useEffect(() => {
    if (!selectedEdition || editionBpasState.loading) return;

    // On load-more, append new rows to the form without resetting existing values.
    // The ref is set synchronously before the state update in handleLoadMore, so it
    // is already true when this effect runs on the resulting re-render.
    if (isLoadMoreRef.current) {
      isLoadMoreRef.current = false;
      const currentFormRows = form.getValues("rows");
      form.setValue("rows", mergeFormRows(currentFormRows, bpaRows));
      bumpFormRowsRevision();
      return;
    }

    // Normal reset: edition change, initial load, or save refetch.
    if (lastSeenEditionConfigIdRef.current !== selectedEdition.id) {
      lastSeenEditionConfigIdRef.current = selectedEdition.id;
      setSelectedRowKeys(clear());
    }
    const serverValues = buildDefaultValues(selectedEdition, bpaRows, savedProductSlugs);
    form.reset(serverValues);
    const retained = retainedFailedEdits.current;
    retainedFailedEdits.current = null;
    const replayable = retained?.editionConfigId === selectedEdition.id ? retained : null;
    if (replayable) {
      applyRetainedEdits(form, serverValues, replayable);
    }
    // The failure notice outlives the edits it belongs to: a save that failed is still worth reporting
    // even once its values have been dropped as belonging to another edition.
    if (!retained) {
      setSaveError(null);
    }
    if (!replayable?.edition) {
      setCascadeStatusToBpas(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedEdition?.id,
    selectedEdition?.status,
    selectedEdition?.leadAssigneeId,
    selectedEdition?.submissionCloseDate,
    selectedEdition?.publicationFromDate,
    selectedEdition?.publicationToDate,
    bpaRows,
  ]);

  // The edition's products arrive on their own request, usually after the reset above has already run
  // with none. Adopting them through another full reset would wipe whatever was edited in between, so
  // only this field's saved value moves. Ticks already made — a failed save's, put back by the reset
  // above — outlive the incoming value rather than being overwritten by it.
  useEffect(() => {
    if (!selectedEdition || !editionProductsQuery.isSuccess) return;
    const isEdited = form.getFieldState("productSlugs").isDirty;
    const edited = form.getValues("productSlugs");
    form.resetField("productSlugs", { defaultValue: savedProductSlugs });
    if (isEdited) {
      form.setValue("productSlugs", edited, { shouldDirty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEdition?.id, savedProductSlugsKey, editionProductsQuery.isSuccess]);

  useEffect(() => {
    if (researchersQuery.isLoading) return;
    const lapsedIds = new Set<string>();
    for (const row of bpaRows) {
      if (row.assignedResearcherId && !researchers.some(r => r.id === row.assignedResearcherId)) {
        lapsedIds.add(row.assignedResearcherId);
      }
    }
    if (
      selectedEdition?.leadAssigneeId &&
      !researchers.some(r => r.id === selectedEdition.leadAssigneeId)
    ) {
      lapsedIds.add(selectedEdition.leadAssigneeId);
    }
    for (const lapsedId of lapsedIds) {
      if (attemptedLookups.current.has(lapsedId)) continue;
      attemptedLookups.current.add(lapsedId);
      userManagement
        .getUserById(lapsedId)
        .then(user => setResolvedNames(prev => ({ ...prev, [lapsedId]: user.name })))
        .catch(error => {
          // AC 34: an unresolvable assignee still reads as assigned via resolveAssignee's fallback name.
          // biome-ignore lint/suspicious/noConsole: surface the lookup failure for diagnosis without breaking the fallback display
          console.warn(`Failed to resolve lapsed assignee ${lapsedId}:`, error);
        });
    }
  }, [bpaRows, researchers, researchersQuery.isLoading, selectedEdition?.leadAssigneeId]);

  const { isDirty } = form.formState;
  const navGuard = useNavigationGuard(isDirty);
  const mergeAction = mergeActionState({
    flagOn: mergeFlagOn,
    canEdit: canAssignResearcher,
    canDelete: hasPermission("staff.research.delete"),
    editionStatus: selectedEdition?.status ?? null,
    isDirty,
    ticked: tickedMergeRows,
    splitOpenIds: tickedSplitQuery.data ?? [],
  });
  const mergeSplitCheckReason = !splitCheckEnabled
    ? null
    : tickedSplitQuery.isError
      ? "Could not check the practice areas for a split. Reload to try again."
      : tickedSplitQuery.isPending
        ? "Checking the practice areas."
        : null;
  const mergeDisabledReason = mergeAction.disabledReason ?? mergeSplitCheckReason;
  const mergeBlockedByMerge =
    mergeAction.disabledReason !== null &&
    mergeAction.disabledReason === inMergeBlockedReason(tickedMergeRows);
  const mergeHubHref =
    selectedEdition && mergeBlockedByMerge
      ? mergeBlockedHubHref({
          editionId: selectedEdition.editionId,
          ticked: tickedMergeRows,
          rows: mergeRows,
        })
      : null;
  const showMergeButton = canSelectRows && mergeAction.visible;
  const showAddBpaButton = canSelectRows && selectedEdition !== undefined;
  const previousEditionId =
    editions.find(edition => edition.id === selectedEdition?.previousEditionId)?.editionId ?? null;
  const editionNamesByEditionId = new Map(
    editions.map(edition => [edition.editionId, edition.editionName ?? edition.editionId]),
  );

  if (!canRead) return <UnauthorizedView />;

  const editionStats = bpaStatsQuery.data?.researchBpaStats ?? null;
  const summary = editionProgressSummary({
    stats: editionStats,
    loadedRows: bpaRows,
    editionTotal: editionBpasState.total,
  });
  const submissionsReceived = sumSubmissionCounts(editionStats);
  const completionPercent =
    summary.total === 0 ? 0 : Math.round((summary.complete / summary.total) * 100);
  const leadAssigneeOptions: AsyncSelectOption[] = assigneeOptions(researchers);
  const editionStatusOptions: AsyncSelectOption[] = EDITION_STATUS_VALUES.map(value => ({
    value,
    label: getStatusLabel(value),
  }));

  function handleDiscard() {
    form.reset();
    setCascadeStatusToBpas(false);
    setSaveError(null);
  }

  function switchEdition(nextEditionConfigId: string) {
    setSelectedEditionConfigId(nextEditionConfigId);
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set("edition", nextEditionConfigId);
        return next;
      },
      { replace: true },
    );
  }

  // Never written against a saved set that did not load: a full replace from an empty guess would
  // clear every product the edition has.
  function hasProductChanges(data: ManageFormValues): boolean {
    return (
      isAdmin &&
      productsEnabled &&
      editionProductsQuery.isSuccess &&
      !sameProductSlugs(data.productSlugs, savedProductSlugs)
    );
  }

  // Sits between validation and the writes: a save that changes products is held for confirmation,
  // every other save goes straight through.
  function handleValidSubmit(data: ManageFormValues) {
    if (!hasProductChanges(data)) return onSubmit(data);
    setProductsChangeRequest({
      values: data,
      adding: researchProducts.filter(
        product =>
          data.productSlugs.includes(product.slug) && !savedProductSlugs.includes(product.slug),
      ),
      removing: researchProducts.filter(
        product =>
          savedProductSlugs.includes(product.slug) && !data.productSlugs.includes(product.slug),
      ),
    });
  }

  async function onSubmit(data: ManageFormValues) {
    if (!selectedEdition) return;
    setSaveError(null);
    setIsSaving(true);
    try {
      const editionPatch: EditionConfigUpdateRequest = {};
      if (data.status !== selectedEdition.status) {
        editionPatch.status = data.status;
        editionPatch.cascadeStatusToBpas = cascadeStatusToBpas;
      }
      if ((data.leadAssigneeId ?? null) !== (selectedEdition.leadAssigneeId ?? null)) {
        editionPatch.leadAssigneeId = data.leadAssigneeId;
      }
      for (const { name } of EDITION_DATE_FIELDS) {
        const saved = toDateInputValue(selectedEdition[name]);
        if ((data[name] ?? null) !== saved) {
          editionPatch[name] = toIsoTimestamp(data[name]);
        }
      }

      const writes: PendingWrite[] = [];
      if (isAdmin && Object.keys(editionPatch).length > 0) {
        writes.push({
          kind: "edition",
          request: withSaveTimeout(() =>
            researchCms.updateEditionConfig(selectedEdition.id, editionPatch),
          ),
        });
      }

      if (hasProductChanges(data)) {
        writes.push({
          kind: "products",
          productSlugs: data.productSlugs,
          // The PUT answers with the saved set, so it seeds the query straight away. Otherwise the
          // rows refetch below can land first and reset the form with the pre-save ticks, which flash
          // back until the products refetch catches up.
          request: withSaveTimeout(async () => {
            const response = await researchCms.updateEditionConfigProducts(selectedEdition.id, {
              productSlugs: data.productSlugs,
            });
            queryClient.setQueryData(
              ["research-edition-config-products", selectedEdition.id],
              response,
            );
            return response;
          }),
        });
      }

      // A `staff.research.create` holder assigns through the bulk endpoint instead of one PATCH per
      // row — bulk-assign requires that permission, which a plain edit-only researcher does not hold.
      // Routing (not replacing) keeps their per-row status write untouched below.
      if (canSelectRows) {
        const bulkPayload = buildBulkAssignPayload(data.rows, bpaRows, editionBpaIdByBpaId);
        if (bulkPayload) {
          const coveredEditionBpaIds = new Set(
            bulkPayload.assignments.map(assignment => assignment.editionBpaId),
          );
          const coveredRows = data.rows.filter(row =>
            coveredEditionBpaIds.has(editionBpaIdByBpaId.get(row.id) ?? ""),
          );
          writes.push({
            kind: "bulk",
            rows: coveredRows,
            request: withSaveTimeout(() => researchCms.bulkAssignEditionBpas(bulkPayload)),
          });
        }
      }

      for (const row of data.rows) {
        const original = bpaRows.find(r => r.id === row.id);
        if (!original) continue;

        const patch: EditionBpaUpdateRequest = {};
        if (
          canAssignResearcher &&
          !canSelectRows &&
          (original.assignedResearcherId ?? null) !== (row.assignedResearcherId ?? null)
        ) {
          patch.assignedResearcherId = row.assignedResearcherId;
        }
        if (
          canAssignResearcher &&
          !canSelectRows &&
          (original.assignedEditorId ?? null) !== (row.assignedEditorId ?? null)
        ) {
          patch.assignedEditorId = row.assignedEditorId;
        }
        if (canEditRowStatus && toRowStatus(original.status) !== row.status) {
          patch.status = row.status;
        }
        if (Object.keys(patch).length === 0) continue;

        const editionBpaId = editionBpaIdByBpaId.get(row.id);
        if (editionBpaId) {
          writes.push({
            kind: "row",
            row,
            request: withSaveTimeout(() => researchCms.updateEditionBpa(editionBpaId, patch)),
          });
        }
      }

      const results = await Promise.allSettled(writes.map(write => write.request));
      const failures = writes.filter((_, index) => results[index].status === "rejected");

      if (failures.length > 0) {
        retainedFailedEdits.current = collectRetainedEdits(data, failures);
        const firstRejection = results.find(result => result.status === "rejected");
        const failedBulkWrite = failures.find(
          (failure): failure is Extract<PendingWrite, { kind: "bulk" }> => failure.kind === "bulk",
        );
        setSaveError(
          buildSaveErrorMessage(
            failures.length,
            writes.length,
            firstRejection?.reason,
            failedBulkWrite
              ? { failed: true, assignmentCount: failedBulkWrite.rows.length }
              : undefined,
          ),
        );
      } else {
        retainedFailedEdits.current = null;
      }
      const productsWrite = writes.find(write => write.kind === "products");
      if (productsWrite && !failures.includes(productsWrite)) {
        setToast({ message: "Product preferences updated", type: "success" });
      }

      await Promise.all([
        editionsQuery.refetch(),
        refetchBpas(),
        ...(productsEnabled ? [editionProductsQuery.refetch()] : []),
      ]);
      if (failures.length === 0) {
        setCascadeStatusToBpas(false);
        setSelectedRowKeys(clear());
      }
    } catch (error) {
      // AC 27: a failed save surfaces the failure and keeps the edited values in place.
      setSaveError(error instanceof Error ? error.message : "Failed to save changes.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeleteAll() {
    if (!selectedEdition) return;
    setIsDeleting(true);
    try {
      await researchCms.deleteAllEditionBpas(selectedEdition.editionId);
      setShowDeleteConfirm(false);
      await refetchBpas();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Failed to delete practice areas.");
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <CmsLayout
      breadcrumbs={[
        ...(selectedEdition
          ? [
              {
                label: publicationConfig?.publicationName ?? "Publication",
                path: ROUTES.research.cmsPublication(selectedEdition.editionId),
              },
            ]
          : []),
        { label: "Manage", path: ROUTES.research.cmsManage(publicationConfigId ?? "") },
      ]}
    >
      <div className={styles.page} data-testid="cms-manage-page">
        <h1 className={styles.pageTitle}>Manage publication</h1>

        <form onSubmit={form.handleSubmit(handleValidSubmit)} className={styles.form}>
          {publicationConfig && (
            <section className={shared.sectionCard} data-testid="publication-details">
              <h2 className={shared.sectionHeading}>Publication details</h2>
              <p className={styles.readOnlyNote}>
                Publication data from taxonomy — read-only. Contact the data team to update.
              </p>
              <div className={styles.detailsGrid}>
                {[
                  { label: "Publication", value: publicationConfig.publicationName ?? "Not set" },
                  { label: "Template type", value: humaniseCode(publicationConfig.templateType) },
                  { label: "Input language", value: languageName(publicationConfig.inputLanguage) },
                  {
                    label: "Output language",
                    value: languageName(publicationConfig.outputLanguage),
                  },
                ].map(detail => (
                  <div className={styles.staticField} key={detail.label}>
                    <span className={styles.staticLabel}>{detail.label}</span>
                    <span className={styles.staticValue}>{detail.value}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Its own card, deliberately slim: this picks WHICH edition everything below describes, so
              it sits apart from the fields it scopes rather than competing with them for width. */}
          <section className={`${shared.sectionCard} ${styles.editionCard}`}>
            <h2 className={shared.sectionHeading}>Edition</h2>
            <p className={styles.editionNote}>
              Everything below applies to the edition selected here.
            </p>
            {/* Locked while a save is in flight, the same as the Save button. A save writes to whichever
                edition was selected when it started, so switching mid-save lands the write on one edition
                while the page shows another. */}
            <Select
              aria-label="Edition"
              fullWidth
              disabled={isSaving}
              value={selectedEditionConfigId ?? ""}
              onChange={e => {
                const nextEditionConfigId = e.target.value;
                if (isDirty) {
                  setPendingEditionSwitchId(nextEditionConfigId);
                  return;
                }
                switchEdition(nextEditionConfigId);
              }}
              options={editions.map(edition => ({
                value: edition.id,
                label: edition.editionName ?? edition.id,
              }))}
            />
          </section>

          {selectedEdition && (
            <>
              <div className={styles.manageTabBar} role="tablist" aria-label="Manage sections">
                <button
                  type="button"
                  role="tab"
                  className={`${styles.manageTab} ${manageTab === "publication" ? styles.manageTabActive : ""}`}
                  aria-selected={manageTab === "publication"}
                  onClick={() => setManageTab("publication")}
                >
                  Publication
                </button>
                <button
                  type="button"
                  role="tab"
                  className={`${styles.manageTab} ${manageTab === "practiceAreas" ? styles.manageTabActive : ""}`}
                  aria-selected={manageTab === "practiceAreas"}
                  onClick={() => setManageTab("practiceAreas")}
                >
                  Practice Areas
                </button>
                {productsEnabled && (
                  <button
                    type="button"
                    role="tab"
                    className={`${styles.manageTab} ${manageTab === "products" ? styles.manageTabActive : ""}`}
                    aria-selected={manageTab === "products"}
                    onClick={() => setManageTab("products")}
                  >
                    Products
                  </button>
                )}
              </div>

              <div style={{ display: manageTab === "publication" ? "contents" : "none" }}>
                <section className={shared.sectionCard}>
                  <h2 className={shared.sectionHeading}>Status, lead &amp; key dates</h2>
                  {isAdmin ? (
                    <div className={styles.editableFields}>
                      <div className={styles.fieldPair}>
                        <Controller
                          name="status"
                          control={form.control}
                          render={({ field }) => (
                            <AsyncSearchableSelect
                              name="status"
                              label="Publication status"
                              helperText="Applies to practice areas that have not been set individually."
                              value={field.value}
                              clearable={false}
                              initialOption={{
                                value: field.value,
                                label: getStatusLabel(field.value),
                              }}
                              onChange={value => {
                                const nextStatus = value as ManageFormValues["status"];
                                if (!nextStatus || nextStatus === field.value) return;
                                if (nextStatus === selectedEdition.status) {
                                  field.onChange(nextStatus);
                                  setCascadeStatusToBpas(false);
                                  return;
                                }
                                const change = describeEditionStatusChange({
                                  previousStatus: selectedEdition.status,
                                  nextStatus,
                                  rows: bpaRows.map(row => ({
                                    id: row.id,
                                    status: row.status,
                                    name: row.name,
                                    rankingEntityName: row.rankingEntityName,
                                  })),
                                  editionStatuses: bpaStatsQuery.data?.researchBpaStats,
                                });
                                setStatusChangeRequest({
                                  nextStatus,
                                  commit: field.onChange,
                                  change,
                                });
                              }}
                              onSearch={async query => {
                                const term = query.trim().toLowerCase();
                                return editionStatusOptions.filter(option =>
                                  term ? option.label.toLowerCase().includes(term) : true,
                                );
                              }}
                              minSearchLength={0}
                            />
                          )}
                        />
                        <Controller
                          name="leadAssigneeId"
                          control={form.control}
                          render={({ field }) => (
                            <AsyncSearchableSelect
                              name="leadAssigneeId"
                              label="Publication lead"
                              value={field.value}
                              onChange={value => field.onChange(value)}
                              onSearch={async query => {
                                const term = query.trim().toLowerCase();
                                return term
                                  ? leadAssigneeOptions.filter(o =>
                                      o.label.toLowerCase().includes(term),
                                    )
                                  : leadAssigneeOptions;
                              }}
                              minSearchLength={0}
                              placeholder="Unassigned"
                              initialOption={
                                field.value
                                  ? (leadAssigneeOptions.find(o => o.value === field.value) ?? null)
                                  : null
                              }
                            />
                          )}
                        />
                      </div>
                      <div className={styles.fieldTriple}>
                        {EDITION_DATE_FIELDS.map(dateField => (
                          <EditionDateField
                            key={dateField.name}
                            control={form.control}
                            name={dateField.name}
                            label={dateField.label}
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className={styles.staticFields}>
                      <div className={styles.fieldPair}>
                        <div className={styles.staticField}>
                          <span className={styles.staticLabel}>Status</span>
                          <StatusBadge status={selectedEdition.status} />
                        </div>
                        <div className={styles.staticField}>
                          <span className={styles.staticLabel}>Publication lead</span>
                          <span className={styles.staticValue}>
                            {resolveAssignee({
                              assigneeId: selectedEdition.leadAssigneeId,
                              researchers,
                              resolvedNames,
                            }).displayName || "Unassigned"}
                          </span>
                        </div>
                      </div>
                      <div className={styles.fieldTriple}>
                        {EDITION_DATE_FIELDS.map(dateField => (
                          <div className={styles.staticField} key={dateField.name}>
                            <span className={styles.staticLabel}>{dateField.label}</span>
                            <span className={styles.staticValue}>
                              {formatDate(selectedEdition[dateField.name])}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </section>

                <section className={shared.sectionCard} data-testid="bpa-progress-summary">
                  <h2 className={shared.sectionHeading}>Progress</h2>
                  <div className={styles.progressHeader}>
                    <div className={styles.progressPercent}>
                      <div className={styles.progressPercentValue}>{completionPercent}%</div>
                      <div className={styles.progressPercentLabel}>Overall completion</div>
                    </div>
                    <div className={styles.progressBarWrap}>
                      <CmsStackedBar
                        complete={summary.complete}
                        inProgress={summary.inProgress}
                        total={summary.total}
                        size="large"
                      />
                    </div>
                  </div>
                  <div className={styles.statGrid}>
                    <div className={styles.statTile}>
                      <span className={styles.statValue}>{summary.total}</span>
                      <span className={styles.statLabel}>Total BPAs</span>
                    </div>
                    <div className={styles.statTile}>
                      <span className={`${styles.statValue} ${styles.statValueComplete}`}>
                        {summary.complete}
                      </span>
                      <span className={styles.statLabel}>Complete</span>
                    </div>
                    <div className={styles.statTile}>
                      <span className={`${styles.statValue} ${styles.statValueInProgress}`}>
                        {summary.inProgress}
                      </span>
                      <span className={styles.statLabel}>In progress</span>
                    </div>
                    <div className={styles.statTile}>
                      <span className={styles.statValue}>{summary.notStarted}</span>
                      <span className={styles.statLabel}>Not started</span>
                    </div>
                    <div className={styles.statTile}>
                      <span className={styles.statValue}>{submissionsReceived ?? "—"}</span>
                      <span className={styles.statLabel}>Submissions received</span>
                    </div>
                  </div>
                  <p className={styles.summaryText}>
                    The completion percentage and bar above count practice areas by status.
                    Submissions received is a different measure: the total number of submissions
                    filed for this edition, with no expected total to compare it against.
                  </p>
                </section>

                <section className={shared.sectionCard}>
                  <h2 className={shared.sectionHeading}>Template fields</h2>
                  <TemplateFieldsPreview
                    fields={templateFields}
                    loading={templateFieldsQuery.isLoading}
                    error={templateFieldsQuery.error ? "Failed to load template fields." : null}
                  />
                </section>
              </div>

              <div style={{ display: manageTab === "practiceAreas" ? "contents" : "none" }}>
                <section className={shared.sectionCard}>
                  <div className={styles.bpaHeader}>
                    <h2
                      className={`${shared.sectionHeading} ${shared.headingInline} ${styles.bpaTitle}`}
                    >
                      BPA assignments &amp; status
                    </h2>
                    {hasTranslations && publicationConfig && (
                      <LanguageSwitcher
                        outputLanguage={publicationConfig.outputLanguage}
                        activeLanguage={
                          showTranslation ? "en-GB" : publicationConfig.outputLanguage
                        }
                        onSwitch={lang => {
                          const next = new URLSearchParams(searchParams);
                          if (lang === "en-GB") next.set("lang", "en-GB");
                          else next.delete("lang");
                          setSearchParams(next, { replace: true });
                        }}
                      />
                    )}
                    {(showMergeButton || showAddBpaButton) && (
                      <div className={styles.bpaActions}>
                        {showMergeButton && (
                          <button
                            type="button"
                            className={styles.importButton}
                            disabled={mergeDisabledReason !== null}
                            aria-describedby={
                              mergeDisabledReason ? "merge-blocked-reason" : undefined
                            }
                            onClick={() => setMergeOpen(true)}
                          >
                            Merge
                          </button>
                        )}
                        {showAddBpaButton && (
                          <button
                            type="button"
                            className={styles.importButton}
                            onClick={() =>
                              navigate(
                                ROUTES.research.cmsCreateBpa(
                                  publicationConfigId ?? "",
                                  selectedEdition.id,
                                ),
                              )
                            }
                          >
                            + Add BPA
                          </button>
                        )}
                      </div>
                    )}
                    {showMergeButton && mergeDisabledReason && (
                      <span className={styles.mergeReasonLine}>
                        <span id="merge-blocked-reason" className={styles.mergeReason}>
                          {mergeDisabledReason}
                        </span>
                        {mergeHubHref && (
                          <Link to={mergeHubHref} className={styles.mergeReasonLink}>
                            Open the merge
                          </Link>
                        )}
                      </span>
                    )}
                    {canSelectRows && (
                      <div
                        className={`${styles.bulkReveal} ${
                          selectedRowKeys.size > 0 ? styles.bulkRevealOpen : ""
                        }`}
                      >
                        <BulkAssignBar
                          open={selectedRowKeys.size > 0}
                          selectedCount={selectedRowKeys.size}
                          researchers={researchers}
                          onAssign={(field, value) => {
                            form.setValue(
                              "rows",
                              applyBulkAssignment(
                                form.getValues("rows"),
                                selectedRowKeys,
                                field,
                                value,
                              ),
                              { shouldDirty: true },
                            );
                          }}
                          onClear={() => setSelectedRowKeys(clear())}
                        />
                      </div>
                    )}
                  </div>
                  {researchersUnavailable && (
                    <p className={styles.researchersUnavailable} role="alert">
                      Researcher list unavailable, so assigned researchers cannot be checked. Reload
                      to try again.
                    </p>
                  )}
                  {editionBpasState.error ? (
                    <div role="alert" className={shared.errorBanner}>
                      {editionBpasState.error}
                    </div>
                  ) : editionBpasState.loading || researchersQuery.isLoading ? (
                    <BpaTablePlaceholder rowCount={placeholderRowCount(bpaRows.length)} />
                  ) : bpaRows.length === 0 ? (
                    <CmsEmptyState message="This edition has no linked practice areas yet." />
                  ) : (
                    <>
                      <CmsGroupedTable
                        groups={[{ name: "", rows: bpaRows }]}
                        rowKey={row => row.id}
                        showHeaders
                        columns={[
                          ...(canSelectRows
                            ? [
                                {
                                  key: "__select",
                                  // The cell's own padding is 10px 16px, so anything under 52px squashes
                                  // the 20px checkbox — it is a flex item and shrinks horizontally while
                                  // keeping its height, rendering as a tall thin rectangle.
                                  width: "56px",
                                  header: (
                                    <SelectAllCheckbox
                                      aria-label="Select all practice areas"
                                      checked={
                                        selectAllState(selectedRowKeys, bpaRows, row => row.id) ===
                                        "all"
                                      }
                                      indeterminate={
                                        selectAllState(selectedRowKeys, bpaRows, row => row.id) ===
                                        "some"
                                      }
                                      onChange={() => {
                                        setSelectedRowKeys(prev =>
                                          selectAllState(prev, bpaRows, row => row.id) === "all"
                                            ? clear()
                                            : selectAll(bpaRows, row => row.id),
                                        );
                                      }}
                                    />
                                  ),
                                  render: (row: BpaRow) => (
                                    <div onClick={e => e.stopPropagation()}>
                                      <Checkbox
                                        aria-label={`Select ${row.name}`}
                                        checked={selectedRowKeys.has(row.id)}
                                        onChange={() =>
                                          setSelectedRowKeys(prev => toggle(prev, row.id))
                                        }
                                        onClick={e => e.stopPropagation()}
                                      />
                                    </div>
                                  ),
                                },
                              ]
                            : []),
                          {
                            key: "name",
                            header: "Business practice area",
                            render: row => {
                              const link = bpaRowLink({
                                editionId: selectedEdition.editionId,
                                row,
                                editionName: selectedEdition.editionName,
                                templateType: publicationConfig?.templateType ?? null,
                              });
                              return (
                                <Link to={link.to} state={link.state}>
                                  {row.name}
                                </Link>
                              );
                            },
                          },
                          {
                            key: "rankingEntity",
                            header: "Ranking entity",
                            width: "130px",
                            render: row => row.rankingEntityName,
                          },
                          {
                            key: "researcher",
                            header: "Assigned researcher",
                            width: "220px",
                            render: row => {
                              const index = form.getValues("rows").findIndex(r => r.id === row.id);
                              if (index === -1) return null;
                              return (
                                <BpaResearcherCell
                                  control={form.control}
                                  index={index}
                                  researchers={researchers}
                                  resolvedNames={resolvedNames}
                                  researchersKnown={researchersKnown}
                                  canAssign={canAssignResearcher}
                                />
                              );
                            },
                          },
                          {
                            key: "editor",
                            header: "Assigned editor",
                            width: "220px",
                            render: row => {
                              const index = form.getValues("rows").findIndex(r => r.id === row.id);
                              if (index === -1) return null;
                              return (
                                <BpaEditorCell
                                  control={form.control}
                                  index={index}
                                  researchers={researchers}
                                  resolvedNames={resolvedNames}
                                  canAssign={canAssignResearcher}
                                />
                              );
                            },
                          },
                          {
                            key: "status",
                            header: "Status",
                            width: "170px",
                            render: row => {
                              const index = form.getValues("rows").findIndex(r => r.id === row.id);
                              if (index === -1) return null;
                              const mergeRow = mergeRows.find(item => item.bpaId === row.id);
                              const badge = mergeRow
                                ? mergeBadge(mergeRow, mergeRows, {
                                    editionId: selectedEdition.editionId,
                                    flagOn: mergeFlagOn,
                                  })
                                : null;
                              const statusCell = (
                                <BpaStatusCell
                                  control={form.control}
                                  index={index}
                                  bpaName={row.name}
                                  canEdit={canEditRowStatus}
                                />
                              );
                              if (!badge) return statusCell;
                              return (
                                <div className={styles.statusWithBadge}>
                                  {statusCell}
                                  <ScheduledForMergeBadge label={badge.label} href={badge.href} />
                                </div>
                              );
                            },
                          },
                          {
                            key: "submissionProgress",
                            header: "Submission progress",
                            width: "150px",
                            render: row => (
                              <span className={styles.submissionCount}>
                                {bpaStatsQuery.isLoading
                                  ? null
                                  : formatSubmissionCount(submissionCountByBpaId.get(row.id))}
                              </span>
                            ),
                          },
                          ...(canAssignResearcher
                            ? [
                                {
                                  key: "__edit",
                                  width: "80px",
                                  header: "",
                                  render: (row: BpaRow) => (
                                    <div style={{ textAlign: "right" }}>
                                      <button
                                        type="button"
                                        className={styles.editRowButton}
                                        onClick={e => {
                                          e.stopPropagation();
                                          navigate(
                                            ROUTES.research.cmsEditBpa(
                                              publicationConfigId ?? "",
                                              row.id,
                                              selectedEdition!.id,
                                            ),
                                          );
                                        }}
                                        aria-label={`Edit ${row.name}`}
                                      >
                                        Edit
                                      </button>
                                    </div>
                                  ),
                                },
                              ]
                            : []),
                        ]}
                      />
                      {editionBpasState.hasMore && (
                        <div className={styles.loadMoreRow}>
                          <button
                            type="button"
                            className={styles.loadMoreButton}
                            onClick={handleLoadMore}
                            disabled={loadingMoreBpas}
                          >
                            {loadingMoreBpas
                              ? "Loading..."
                              : `Load more (${editionBpasState.total - editionBpas.length} remaining)`}
                          </button>
                          <button
                            type="button"
                            className={styles.loadMoreButton}
                            onClick={handleLoadAll}
                            disabled={loadingMoreBpas}
                          >
                            {loadingMoreBpas ? "Loading..." : "Load all"}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </section>

                {isAdmin && bpaRows.length > 0 && (
                  <DangerZone
                    heading="Danger zone"
                    buttonLabel="Clear all BPAs"
                    onAction={() => setShowDeleteConfirm(true)}
                  >
                    Remove all BPAs and their ranking tables from this edition. This cannot be
                    undone.
                  </DangerZone>
                )}
              </div>

              {productsEnabled && (
                <div style={{ display: manageTab === "products" ? "contents" : "none" }}>
                  <section className={shared.sectionCard}>
                    <div className={styles.bpaHeader}>
                      <h2
                        className={`${shared.sectionHeading} ${shared.headingInline}`}
                        style={{ flex: 1 }}
                        id="products-heading"
                      >
                        Products for {selectedEdition.editionName ?? "this edition"}
                      </h2>
                      {isAdmin && (
                        <button
                          type="button"
                          className={styles.addProductButton}
                          onClick={() => setShowAddProduct(true)}
                        >
                          + Add product
                        </button>
                      )}
                    </div>
                    {/* Mounted whatever the request state, so the field is registered by the time the
                        edition's products land and the effect above can move its saved value. */}
                    <Controller
                      name="productSlugs"
                      control={form.control}
                      render={({ field }) =>
                        researchProductsQuery.error || editionProductsQuery.error ? (
                          <p className={styles.productsUnavailable} role="alert">
                            Products unavailable. Reload to try again.
                          </p>
                        ) : researchProductsQuery.isLoading ||
                          editionProductsQuery.isLoading ||
                          // Held until the rows land too: their arrival resets the whole form, which
                          // would take a tick made before it with it.
                          editionBpasState.loading ? (
                          <CmsEmptyState message="Loading products…" loading />
                        ) : researchProducts.length === 0 ? (
                          <CmsEmptyState message="No products are set up yet." />
                        ) : (
                          <div
                            className={styles.productList}
                            role="group"
                            aria-labelledby="products-heading"
                          >
                            {researchProducts.map(product => (
                              <Checkbox
                                key={product.slug}
                                id={`product-${product.slug}`}
                                aria-label={product.label}
                                label={product.label}
                                checked={field.value.includes(product.slug)}
                                disabled={!isAdmin || productsChangeRequest !== null}
                                onChange={e =>
                                  field.onChange(
                                    toggleProductSlug(
                                      researchProducts,
                                      field.value,
                                      product.slug,
                                      e.target.checked,
                                    ),
                                  )
                                }
                              />
                            ))}
                          </div>
                        )
                      }
                    />
                  </section>
                </div>
              )}
            </>
          )}

          {saveError && (
            <div className={styles.saveError} role="alert">
              {saveError}
            </div>
          )}

          <div className={shared.footer}>
            {isDirty && (
              <button type="button" className={shared.discardButton} onClick={handleDiscard}>
                Discard changes
              </button>
            )}
            <button type="submit" className={shared.saveButton} disabled={isSaving}>
              {isSaving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>

      {showDeleteConfirm && selectedEdition && (
        <CmsConfirmDialog
          title="Clear all practice areas"
          message={`Delete all ${editionBpasState.total} practice areas and their ranking tables from ${selectedEdition.editionName ?? "this edition"}? This cannot be undone.`}
          tone="danger"
          confirmLabel={isDeleting ? "Deleting…" : "Delete all"}
          cancelLabel="Cancel"
          onCancel={() => {
            if (!isDeleting) setShowDeleteConfirm(false);
          }}
          onConfirm={handleDeleteAll}
        />
      )}

      {statusChangeRequest && (
        <CmsConfirmDialog
          title={
            statusChangeRequest.change.direction === "backward"
              ? "Move edition status backward"
              : "Change edition status"
          }
          message={`Change the edition status to ${getStatusLabel(statusChangeRequest.nextStatus)}?`}
          detail={buildStatusChangeWarning(
            statusChangeRequest.change,
            getStatusLabel(statusChangeRequest.nextStatus),
          )}
          affected={buildStatusChangeAffected(statusChangeRequest.change)}
          tone={statusChangeRequest.change.direction === "backward" ? "danger" : "warning"}
          confirmLabel={
            statusChangeRequest.change.direction === "backward"
              ? "Move back anyway"
              : "Change status"
          }
          cancelLabel="Cancel"
          onCancel={() => setStatusChangeRequest(null)}
          onConfirm={() => {
            statusChangeRequest.commit(statusChangeRequest.nextStatus);
            setCascadeStatusToBpas(statusChangeRequest.change.requestsCascade);
            setStatusChangeRequest(null);
          }}
        />
      )}

      {productsChangeRequest && (
        <CmsConfirmDialog
          title="Update products"
          message={`Update products for ${selectedEdition?.editionName ?? "this edition"}?`}
          affected={buildProductsChangeAffected(productsChangeRequest)}
          tone="warning"
          confirmLabel="Confirm"
          cancelLabel="Cancel"
          onCancel={() => setProductsChangeRequest(null)}
          onConfirm={() => {
            const { values } = productsChangeRequest;
            setProductsChangeRequest(null);
            void onSubmit(values);
          }}
        />
      )}

      {pendingEditionSwitchId !== null && (
        <UnsavedChangesModal
          onCancel={() => setPendingEditionSwitchId(null)}
          onDiscard={() => {
            const nextEditionConfigId = pendingEditionSwitchId;
            setPendingEditionSwitchId(null);
            switchEdition(nextEditionConfigId);
          }}
        />
      )}

      {pendingEditionSwitchId === null && navGuard.blocked && (
        <UnsavedChangesModal
          onCancel={navGuard.cancel}
          onDiscard={() => {
            handleDiscard();
            navGuard.proceed();
          }}
        />
      )}

      {/* Outside the edition form: the modal submits its own form, and its name field must not
          dirty or submit the edition's. */}
      <CreateResearchProductModal
        open={showAddProduct}
        onClose={() => setShowAddProduct(false)}
        onCreated={() => {
          queryClient.invalidateQueries({ queryKey: ["research-products"] });
          setToast({ message: "Product added", type: "success" });
        }}
      />

      {mergeOpen && selectedEdition && (
        <CmsBpaMergeDialog
          editionId={selectedEdition.editionId}
          previousEditionId={previousEditionId}
          editionNamesByEditionId={editionNamesByEditionId}
          ticked={tickedMergeRows}
          onClose={createdTarget => {
            setMergeOpen(false);
            if (createdTarget) refetchBpas();
          }}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </CmsLayout>
  );
}
